import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  CopyButton,
  Group,
  List,
  Loader,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconCalendarPlus, IconCheck, IconCopy, IconDownload, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import HttpClient from '../helpers/HttpClient';
import { notify } from '../components/NotificationUtils';

interface CalendarFeed {
  id: number;
  name: string;
  token: string;
  matchTeams: string[];
  trainingTagIds: number[];
  lastAccessedAt: string | null;
}

interface FeedSuggestion {
  personId: number;
  firstName: string;
  matchTeams: string[];
  trainingTagIds: number[];
}

interface CalendarOptions {
  matchTeams: string[];
  trainingTags: { id: number; name: string }[];
  suggestions: FeedSuggestion[];
}

const MAX_FEEDS = 10;

// Absolute feed URL — calendar apps fetch it themselves, without login.
const feedUrlFor = (token: string) =>
  `${new URL(import.meta.env.VITE_X_API_URL, window.location.origin).href.replace(/\/$/, '')}/calendar/ics/${token}.ics`;

const sameItems = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Name for a suggested calendar, e.g. "Anna – U10". */
const suggestionName = (s: FeedSuggestion, options: CalendarOptions) => {
  const tagNames = options.trainingTags.filter((t) => s.trainingTagIds.includes(t.id)).map((t) => t.name);
  const teams = s.matchTeams.length ? s.matchTeams : tagNames;
  return teams.length ? `${s.firstName} – ${teams.join(', ')}` : s.firstName;
};

/** Kalender-Abo: matches and trainings of chosen teams as subscribable .ics feeds, several per person. */
function CalendarSubscription() {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<CalendarOptions | null>(null);
  const [feeds, setFeeds] = useState<CalendarFeed[]>([]);
  // A new, not yet saved calendar (at most one at a time).
  const [draft, setDraft] = useState(false);

  useEffect(() => {
    Promise.all([
      new HttpClient<CalendarOptions>('/calendar/options').getSingle(),
      new HttpClient<CalendarFeed>('/calendar/feeds').getAll(),
    ])
      .then(([opts, list]) => {
        setOptions(opts);
        setFeeds(list);
        setDraft(list.length === 0);
      })
      .catch(() => notify('Fehler', 'Kalender-Einstellungen konnten nicht geladen werden.', 'red'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader size="md" mt="xl" />;
  if (!options) return null;

  return (
    <Box mx="auto" maw={800} p="md">
      <Title order={2} mb="xs">Kalender-Abo</Title>
      <Text c="dimmed" mb="md">
        Spiele und Trainings deiner Mannschaften direkt im Kalender am Handy oder PC. Änderungen und
        Absagen werden automatisch übernommen. Du kannst mehrere Kalender anlegen – z.B. einen pro Kind,
        jeder mit eigenem Link.
      </Text>

      <Stack gap="md">
        {feeds.map((feed) => (
          <FeedCard
            key={feed.id}
            feed={feed}
            options={options}
            onSaved={(saved) => setFeeds((list) => list.map((f) => (f.id === saved.id ? saved : f)))}
            onDeleted={() => setFeeds((list) => list.filter((f) => f.id !== feed.id))}
          />
        ))}

        {draft && (
          <FeedCard
            options={options}
            onSaved={(saved) => {
              setFeeds((list) => [...list, saved]);
              setDraft(false);
            }}
            onCancel={feeds.length ? () => setDraft(false) : undefined}
          />
        )}

        {!draft && feeds.length < MAX_FEEDS && (
          <Group>
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setDraft(true)}>
              Neuer Kalender
            </Button>
          </Group>
        )}
      </Stack>
    </Box>
  );
}

interface FeedCardProps {
  feed?: CalendarFeed;
  options: CalendarOptions;
  onSaved: (feed: CalendarFeed) => void;
  onDeleted?: () => void;
  onCancel?: () => void;
}

/** One calendar: name, team selection and — once saved — its link. Without `feed` it creates a new one. */
function FeedCard({ feed, options, onSaved, onDeleted, onCancel }: FeedCardProps) {
  const initial = useMemo(() => {
    if (feed) return { name: feed.name, matchTeams: feed.matchTeams, tagIds: feed.trainingTagIds.map(String) };
    // New calendar: start from the first suggestion (the person or their first child).
    const s = options.suggestions[0];
    return s
      ? { name: suggestionName(s, options), matchTeams: s.matchTeams, tagIds: s.trainingTagIds.map(String) }
      : { name: '', matchTeams: [] as string[], tagIds: [] as string[] };
  }, [feed, options]);

  const [name, setName] = useState(initial.name);
  const [matchTeams, setMatchTeams] = useState<string[]>(initial.matchTeams);
  const [tagIds, setTagIds] = useState<string[]>(initial.tagIds);
  const [suggestion, setSuggestion] = useState<string | null>(
    !feed && options.suggestions[0] ? String(options.suggestions[0].personId) : null,
  );
  const [saving, setSaving] = useState(false);

  const dirty = !feed
    || name.trim() !== feed.name
    || !sameItems(matchTeams, feed.matchTeams)
    || !sameItems(tagIds, feed.trainingTagIds.map(String));
  const nothingSelected = matchTeams.length === 0 && tagIds.length === 0;

  const applySuggestion = (value: string | null) => {
    setSuggestion(value);
    const s = options.suggestions.find((x) => String(x.personId) === value);
    if (!s) return;
    setName(suggestionName(s, options));
    setMatchTeams(s.matchTeams);
    setTagIds(s.trainingTagIds.map(String));
  };

  const save = async () => {
    setSaving(true);
    try {
      // The endpoints return the feed itself (no { status, data } wrapper).
      const saved = (await new HttpClient<CalendarFeed>('/calendar').postAction(
        feed ? `feeds/${feed.id}` : 'feeds',
        { name: name.trim(), matchTeams, trainingTagIds: tagIds.map(Number) },
      )) as unknown as CalendarFeed;
      onSaved(saved);
      notify('Gespeichert', feed ? 'Der Kalender wurde aktualisiert.' : 'Der Kalender-Link wurde erstellt.', 'green');
    } catch {
      notify('Fehler', 'Speichern fehlgeschlagen.', 'red');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!feed) return;
    if (!window.confirm('Neuen Link erstellen? Der alte Link funktioniert danach nicht mehr – bereits eingerichtete Kalender musst du neu abonnieren.')) return;
    try {
      const renewed = (await new HttpClient<CalendarFeed>('/calendar').postAction(`feeds/${feed.id}/regenerate`)) as unknown as CalendarFeed;
      onSaved(renewed);
      notify('Neuer Link', 'Der alte Link ist ab sofort ungültig.', 'green');
    } catch {
      notify('Fehler', 'Link konnte nicht erneuert werden.', 'red');
    }
  };

  const remove = async () => {
    if (!feed) return;
    if (!window.confirm(`Kalender „${feed.name}“ löschen? Der Link funktioniert danach nicht mehr.`)) return;
    try {
      await new HttpClient<CalendarFeed>('/calendar/feeds').delete(feed.id);
      onDeleted?.();
    } catch {
      notify('Fehler', 'Löschen fehlgeschlagen.', 'red');
    }
  };

  const feedUrl = feed ? feedUrlFor(feed.token) : null;
  const webcalUrl = feedUrl?.replace(/^https?:\/\//, 'webcal://');

  return (
    <Paper withBorder p="md" radius="sm">
      <Group justify="space-between" mb="sm">
        <Text fw={600}>{feed ? feed.name : 'Neuer Kalender'}</Text>
        {feed && (
          <Button variant="subtle" color="red" size="xs" leftSection={<IconTrash size={14} />} onClick={remove}>
            Löschen
          </Button>
        )}
      </Group>

      <Stack gap="sm" mb={feed ? 'md' : 0}>
        {!feed && options.suggestions.length > 0 && (
          <Select
            label="Vorschlag für"
            data={options.suggestions.map((s) => ({ value: String(s.personId), label: s.firstName }))}
            value={suggestion}
            onChange={applySuggestion}
            clearable
            placeholder="Selbst auswählen"
          />
        )}
        <TextInput
          label="Name im Kalender"
          placeholder="z.B. Anna – U10"
          value={name}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setName(value);
          }}
        />
        <MultiSelect
          label="Spiele der Mannschaften"
          placeholder="Mannschaften wählen"
          data={options.matchTeams}
          value={matchTeams}
          onChange={setMatchTeams}
          searchable
          clearable
        />
        <MultiSelect
          label="Trainings der Mannschaften"
          placeholder="Mannschaften wählen"
          data={options.trainingTags.map((t) => ({ value: String(t.id), label: t.name }))}
          value={tagIds}
          onChange={setTagIds}
          searchable
          clearable
        />
        <Group>
          <Button onClick={save} loading={saving} disabled={!dirty || nothingSelected}>
            {feed ? 'Änderungen speichern' : 'Kalender-Link erstellen'}
          </Button>
          {onCancel && <Button variant="default" onClick={onCancel}>Abbrechen</Button>}
          {nothingSelected && <Text size="sm" c="dimmed">Wähle mindestens eine Mannschaft.</Text>}
        </Group>
      </Stack>

      {feed && feedUrl && webcalUrl && (
        <>
          {dirty && (
            <Alert color="yellow" mb="sm">
              Die Änderungen sind noch nicht gespeichert.
            </Alert>
          )}
          <Group gap="xs" mb="sm" wrap="nowrap">
            <TextInput value={feedUrl} readOnly style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
            <CopyButton value={feedUrl}>
              {({ copied, copy }) => (
                <Button
                  variant="light"
                  color={copied ? 'teal' : 'blue'}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  onClick={copy}
                >
                  {copied ? 'Kopiert' : 'Kopieren'}
                </Button>
              )}
            </CopyButton>
          </Group>

          <Group gap="xs" mb="md">
            <Button component="a" href={webcalUrl} leftSection={<IconCalendarPlus size={16} />}>
              Im Kalender abonnieren
            </Button>
            <Button
              component="a"
              href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="default"
            >
              Google Kalender
            </Button>
            <Button
              component="a"
              href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl)}&name=${encodeURIComponent(feed.name)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="default"
            >
              Outlook
            </Button>
            <Button component="a" href={feedUrl} download={`${feed.name}.ics`} variant="subtle" leftSection={<IconDownload size={16} />}>
              .ics herunterladen
            </Button>
          </Group>

          <List size="sm" spacing={4} mb="sm">
            <List.Item><strong>iPhone/Mac:</strong> „Im Kalender abonnieren“ tippen und bestätigen.</List.Item>
            <List.Item><strong>Android/Google:</strong> „Google Kalender“ öffnen und „Hinzufügen“ wählen. Google aktualisiert Abos nur etwa alle 12–24 Stunden.</List.Item>
            <List.Item><strong>Outlook:</strong> „Outlook“ öffnen oder den Link unter „Kalender hinzufügen → Aus dem Internet“ einfügen.</List.Item>
          </List>

          <Text size="xs" c="dimmed" mb="xs">
            Der Link funktioniert ohne Anmeldung – gib ihn nur an Personen weiter, die diese Termine sehen dürfen.
            {feed.lastAccessedAt && ` Zuletzt abgerufen: ${dayjs(feed.lastAccessedAt).format('DD.MM.YYYY HH:mm')}.`}
          </Text>
          <Anchor component="button" size="sm" c="red" onClick={regenerate}>
            <Group gap={4}><IconRefresh size={14} /> Link neu erstellen</Group>
          </Anchor>
        </>
      )}
    </Paper>
  );
}

export default CalendarSubscription;
